import React, { useState, useEffect, useCallback } from 'react';
import './CalendarPage.css';
import PMDateEditModal from './PMDateEditModal';

const CalendarPage = ({ initialDate, initialView, refreshTrigger }) => {
  const [currentDate, setCurrentDate] = useState(() => initialDate ? new Date(initialDate) : new Date());
  const [selectedDate, setSelectedDate] = useState(() => initialDate ? new Date(initialDate) : new Date());
  const [deadlines, setDeadlines] = useState([]);
  const [pmSchedules, setPmSchedules] = useState([]);
  const [filteredDeadlines, setFilteredDeadlines] = useState([]);
  const [viewMode, setViewMode] = useState(initialView || 'month'); // month, week, day, list
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedDeadline, setSelectedDeadline] = useState(null);
  const [showPMDateEditModal, setShowPMDateEditModal] = useState(false);
  const [selectedPMData, setSelectedPMData] = useState(null);
  const [filters, setFilters] = useState({
    priority: '',
    category: '',
    status: '',
    dateRange: 'all'
  });
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});

  // Priority colors
  const priorityColors = {
    low: '#4CAF50',
    medium: '#FF9800',
    high: '#F44336',
    urgent: '#9C27B0'
  };

  // Status colors
  const statusColors = {
    pending: '#2196F3',
    scheduled: '#00BCD4',
    in_progress: '#FF9800',
    completed: '#4CAF50',
    cancelled: '#757575'
  };

  useEffect(() => {
    fetchDeadlines();
    fetchPMSchedules();
    fetchStats();
  }, []);

  useEffect(() => {
    if (initialDate) {
      const d = new Date(initialDate);
      if (!isNaN(d)) {
        setCurrentDate(d);
        setSelectedDate(d);
      }
    }
    if (initialView) {
      setViewMode(initialView);
    }
  }, [initialDate, initialView]);

  useEffect(() => {
    if (refreshTrigger) {
      fetchDeadlines();
      fetchPMSchedules();
    }
  }, [refreshTrigger]);

  const applyFilters = useCallback(() => {
    // Combine deadlines and PM schedules
    let filtered = [...deadlines, ...pmSchedules];

    // Hide completed items by default
    filtered = filtered.filter(d => d.status !== 'completed');

    // Filter by priority
    if (filters.priority) {
      filtered = filtered.filter(d => d.priority === filters.priority);
    }

    // Filter by category
    if (filters.category) {
      filtered = filtered.filter(d => d.category === filters.category);
    }

    // Filter by status
    if (filters.status) {
      if (filters.status === 'scheduled_pending') {
        filtered = filtered.filter(d => d.status === 'scheduled' || d.status === 'pending');
      } else {
        filtered = filtered.filter(d => d.status === filters.status);
      }
    }

    // Filter by date range
    if (filters.dateRange !== 'all') {
      const now = new Date();
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

      switch (filters.dateRange) {
        case 'today':
          filtered = filtered.filter(d => {
            const dueDate = new Date(d.due_date || d.next_inspection_date);
            return !isNaN(dueDate) && dueDate.toDateString() === now.toDateString();
          });
          break;
        case 'week':
          const weekStart = new Date(now);
          weekStart.setDate(now.getDate() - now.getDay());
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekStart.getDate() + 6);
          filtered = filtered.filter(d => {
            const dueDate = new Date(d.due_date || d.next_inspection_date);
            return !isNaN(dueDate) && dueDate >= weekStart && dueDate <= weekEnd;
          });
          break;
        case 'month':
          filtered = filtered.filter(d => {
            const dueDate = new Date(d.due_date || d.next_inspection_date);
            return !isNaN(dueDate) && dueDate >= startOfMonth && dueDate <= endOfMonth;
          });
          break;
        default:
          break;
      }
    }

    setFilteredDeadlines(filtered);
  }, [deadlines, pmSchedules, filters, currentDate]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters, viewMode]);

  const fetchDeadlines = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/deadlines');
      const data = await response.json();
      // Normalize deadlines to ensure due_date is parseable and defaults are present
      const normalized = (data || []).map((d) => {
        let due = d.due_date;
        try {
          // Handle Thai format like "dd/mm/2569, HH:MM" or "dd/mm/2569"
          if (typeof due === 'string' && due.includes('/')) {
            const [datePart, timePart] = due.split(',').map(s => s.trim());
            const [dd, mm, yyyyStr] = datePart.split('/');
            const yyyy = parseInt(yyyyStr, 10);
            if (!isNaN(yyyy) && yyyy > 2400) {
              const gYear = yyyy - 543;
              let hours = 0, minutes = 0;
              if (timePart && /\d{1,2}:\d{2}/.test(timePart)) {
                const [hh, min] = timePart.split(':');
                hours = parseInt(hh, 10);
                minutes = parseInt(min, 10);
              }
              const dt = new Date(gYear, parseInt(mm, 10) - 1, parseInt(dd, 10), hours, minutes);
              if (!isNaN(dt.getTime())) {
                due = dt.toISOString();
              }
            }
          }
        } catch (_) {
          // keep original
        }
        return {
          ...d,
          due_date: due,
          category: d.category || 'general',
          priority: d.priority || 'medium',
          status: d.status || 'pending',
        };
      });
      setDeadlines(normalized);
    } catch (error) {
      console.error('Error fetching deadlines:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPMSchedules = async () => {
    try {
      // Request all statuses so completed items also show up on the calendar
      const response = await fetch('/api/pm-service/schedules?status=all');
      const data = await response.json();
      // Use PM schedules directly without conversion
      const pmSchedules = data
        .filter(schedule => schedule.next_inspection_date) // Filter out schedules without valid dates
        .map(schedule => ({
          id: `pm_${schedule.id}`,
          title: `PM ${schedule.service_number || 'undefined'} - ${schedule.serial_number}`,
          description: `ตรวจสอบ PM ครั้งที่ ${schedule.service_number || 'undefined'} สำหรับ ${schedule.product_type || 'อุปกรณ์'} หมายเลข ${schedule.serial_number}`,
          due_date: schedule.next_inspection_date,
          next_inspection_date: schedule.next_inspection_date,
          priority: schedule.priority || 'medium',
          category: 'PM',
          status: schedule.status || 'pending',
          pm_schedule_id: schedule.id,
          serial_number: schedule.serial_number,
          service_number: schedule.service_number,
          product_type: schedule.product_type,
          customer_name: schedule.customer_name || schedule.customer_name_from_sale,
          reminder_days: schedule.reminder_days || 7,
          notification_sent: schedule.notification_sent || false,
          notes: schedule.notes || ''
        }));
      setPmSchedules(pmSchedules);
    } catch (error) {
      console.error('Error fetching PM schedules:', error);
    }
  };

  const calculateStats = useCallback(() => {
    // Combine all deadlines and PM schedules
    const allItems = [...deadlines, ...pmSchedules];
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to compare dates only

    let total = 0;
    let pending = 0;
    let inProgress = 0;
    let overdue = 0;

    allItems.forEach(item => {
      // Skip completed items for stats calculation
      if (item.status === 'completed') return;
      
      total++;
      
      // Calculate status
      if (item.status === 'pending' || item.status === 'scheduled') {
        pending++;
      } else if (item.status === 'in_progress') {
        inProgress++;
      }

      // Check if overdue
      const dueDate = new Date(item.due_date || item.next_inspection_date);
      if (!isNaN(dueDate.getTime())) {
        dueDate.setHours(0, 0, 0, 0);
        if (dueDate < today && item.status !== 'completed') {
          overdue++;
        }
      }
    });

    setStats({
      total,
      pending,
      in_progress: inProgress,
      overdue
    });
  }, [deadlines, pmSchedules]);

  useEffect(() => {
    calculateStats();
  }, [calculateStats]);

  const fetchStats = async () => {
    // Keep the original API call as backup, but use calculated stats
    try {
      const response = await fetch('/api/deadlines/stats');
      const data = await response.json();
      // Don't use this data, we calculate our own
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getDeadlinesForDate = (date) => {
    if (!date) return [];
    return filteredDeadlines.filter(deadline => {
      const base = deadline.due_date || deadline.next_inspection_date;
      const deadlineDate = new Date(base);
      if (isNaN(deadlineDate)) return false;
      return deadlineDate.toDateString() === date.toDateString();
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'ไม่ระบุเวลา';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'ไม่ระบุเวลา';
    return date.toLocaleTimeString('th-TH', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // removed unused date conversion helpers

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
  };

  const handleAddDeadline = () => {
    setSelectedDeadline(null);
    setShowAddModal(true);
  };

  const handleEditDeadline = (deadline) => {
    setSelectedDeadline(deadline);
    setShowAddModal(true);
  };

  const handleOpenPMDateEdit = (deadline) => {
    setSelectedPMData({
      pm_schedule_id: deadline.pm_schedule_id,
      serial_number: deadline.serial_number,
      customer_name: deadline.customer_name,
      product_type: deadline.product_type,
      due_date: deadline.due_date,
      title: deadline.title,
      description: deadline.description
    });
    setShowPMDateEditModal(true);
  };

  const handleSavePMDate = async (updateData) => {
    try {
      const response = await fetch(`/api/pm-service/schedules/${updateData.pm_schedule_id}/update-date`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          next_inspection_date: updateData.new_date,
          last_service_date: updateData.last_service_date,
          calculated_date: updateData.calculated_date,
          use_calculated: updateData.use_calculated,
          notes: updateData.notes,
          updated_by: 1 // TODO: Get current user ID
        }),
      });

      if (response.ok) {
        // Refresh data
        fetchPMSchedules();
        fetchDeadlines();
        alert('อัปเดตวันที่กำหนด PM สำเร็จ');
        // Close the modal
        setShowPMDateEditModal(false);
        setSelectedPMData(null);
      } else {
        const error = await response.json();
        alert(`เกิดข้อผิดพลาด: ${error.error || 'ไม่สามารถอัปเดตวันที่ได้'}`);
        throw new Error(error.error || 'เกิดข้อผิดพลาดในการอัปเดต');
      }
    } catch (error) {
      console.error('Error updating PM date:', error);
      if (!error.message.includes('เกิดข้อผิดพลาด:')) {
        alert('เกิดข้อผิดพลาดในการเชื่อมต่อเซิร์ฟเวอร์');
      }
      throw error;
    }
  };

  const handleDeletePMSchedule = async (deadline) => {
    // ยืนยันการลบ
    const confirmDelete = window.confirm(
      `คุณต้องการลบ PM Schedule นี้หรือไม่?\n\n` +
      `รายการ: ${deadline.title}\n` +
      `หมายเลขซีเรียล: ${deadline.serial_number}\n` +
      `วันที่กำหนด: ${new Date(deadline.due_date).toLocaleDateString('th-TH')}\n\n` +
      `การลบนี้ไม่สามารถยกเลิกได้`
    );

    if (!confirmDelete) {
      return;
    }

    try {
      const response = await fetch(`/api/pm-service/schedules/${deadline.pm_schedule_id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        // Refresh data
        fetchPMSchedules();
        fetchDeadlines();
        calculateStats();
        alert('ลบ PM Schedule สำเร็จ');
      } else {
        const error = await response.json();
        alert(`เกิดข้อผิดพลาดในการลบ: ${error.error || 'ไม่สามารถลบได้'}`);
      }
    } catch (error) {
      console.error('Error deleting PM schedule:', error);
      alert('เกิดข้อผิดพลาดในการลบ PM Schedule');
    }
  };

  const handleDeleteDeadline = async (deadline) => {
    // ยืนยันการลบ
    const confirmDelete = window.confirm(
      `คุณต้องการลบ Deadline นี้หรือไม่?\n\n` +
      `รายการ: ${deadline.title}\n` +
      `คำอธิบาย: ${deadline.description || 'ไม่มี'}\n` +
      `วันที่กำหนด: ${new Date(deadline.due_date).toLocaleDateString('th-TH')}\n` +
      `ระดับความสำคัญ: ${deadline.priority === 'low' ? 'ต่ำ' :
                        deadline.priority === 'medium' ? 'ปานกลาง' :
                        deadline.priority === 'high' ? 'สูง' : 'เร่งด่วน'}\n\n` +
      `การลบนี้ไม่สามารถยกเลิกได้`
    );

    if (!confirmDelete) {
      return;
    }

    try {
      const response = await fetch(`/api/deadlines/${deadline.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        // Refresh data
        fetchDeadlines();
        calculateStats();
        alert('ลบ Deadline สำเร็จ');
      } else {
        const error = await response.json();
        alert(`เกิดข้อผิดพลาดในการลบ: ${error.error || 'ไม่สามารถลบได้'}`);
      }
    } catch (error) {
      console.error('Error deleting deadline:', error);
      alert('เกิดข้อผิดพลาดในการลบ Deadline');
    }
  };

  const goToNextUpcomingPM = () => {
    if (!pmSchedules || pmSchedules.length === 0) return;
    const today = new Date();
    const upcoming = pmSchedules
      .map(d => ({ ...d, _date: new Date(d.due_date) }))
      .filter(d => !isNaN(d._date.getTime()) && d._date >= new Date(today.toDateString()))
      .sort((a, b) => a._date - b._date)[0];
    if (upcoming) {
      setCurrentDate(upcoming._date);
      setSelectedDate(upcoming._date);
    }
  };

  const renderCalendarHeader = () => (
    <div className="calendar-header">
      <div className="calendar-navigation">
        <button onClick={() => navigateMonth(-1)} className="nav-btn">
          <span>‹</span>
        </button>
        <h2 className="current-month">
          {currentDate.toLocaleDateString('th-TH', { year: 'numeric', month: 'long' })}
        </h2>
        <button onClick={() => navigateMonth(1)} className="nav-btn">
          <span>›</span>
        </button>
      </div>
      
      <div className="calendar-controls">
        <button onClick={goToToday} className="today-btn">
          วันนี้
        </button>
        <button onClick={goToNextUpcomingPM} className="today-btn" title="ไปยัง PM ถัดไป">
          PM ถัดไป
        </button>
        
        <div className="view-mode-selector">
          {['month', 'week', 'day', 'list'].map(mode => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={`view-btn ${viewMode === mode ? 'active' : ''}`}
            >
              {mode === 'month' ? 'เดือน' : 
               mode === 'week' ? 'สัปดาห์' : 
               mode === 'day' ? 'วัน' : 'รายการ'}
            </button>
          ))}
        </div>
        
        <button onClick={handleAddDeadline} className="add-deadline-btn">
          + เพิ่มวันที่กำหนด
        </button>
      </div>
    </div>
  );

  const renderFilters = () => (
    <div className="filters-section">
      <div className="filter-group">
        <label>ระดับความสำคัญ:</label>
        <select 
          value={filters.priority} 
          onChange={(e) => setFilters({...filters, priority: e.target.value})}
        >
          <option value="">ทั้งหมด</option>
          <option value="low">ต่ำ</option>
          <option value="medium">ปานกลาง</option>
          <option value="high">สูง</option>
          <option value="urgent">เร่งด่วน</option>
        </select>
      </div>
      
      <div className="filter-group">
        <label>หมวดหมู่:</label>
        <select 
          value={filters.category} 
          onChange={(e) => setFilters({...filters, category: e.target.value})}
        >
          <option value="">ทั้งหมด</option>
          <option value="PM">PM (Preventive Maintenance)</option>
          <option value="maintenance">การบำรุงรักษา</option>
          <option value="installation">การติดตั้ง</option>
          <option value="report">รายงาน</option>
          <option value="training">การอบรม</option>
          <option value="inventory">สต็อกสินค้า</option>
        </select>
      </div>
      
      <div className="filter-group">
        <label>สถานะ:</label>
        <select 
          value={filters.status} 
          onChange={(e) => setFilters({...filters, status: e.target.value})}
        >
          <option value="">ทั้งหมด</option>
          <option value="scheduled_pending">กำหนดการ</option>
          <option value="in_progress">กำลังดำเนินการ</option>
          <option value="cancelled">ยกเลิก</option>
        </select>
      </div>
      
      <div className="filter-group">
        <label>ช่วงเวลา:</label>
        <select 
          value={filters.dateRange} 
          onChange={(e) => setFilters({...filters, dateRange: e.target.value})}
        >
          <option value="all">ทั้งหมด</option>
          <option value="today">วันนี้</option>
          <option value="week">สัปดาห์นี้</option>
          <option value="month">เดือนนี้</option>
        </select>
      </div>
    </div>
  );

  const renderStats = () => (
    <div className="stats-section">
      <div className="stat-card">
        <div className="stat-number">{stats.total || 0}</div>
        <div className="stat-label">ทั้งหมด</div>
      </div>
      <div className="stat-card pending">
        <div className="stat-number">{stats.pending || 0}</div>
        <div className="stat-label">กำหนดการ</div>
      </div>
      <div className="stat-card in-progress">
        <div className="stat-number">{stats.in_progress || 0}</div>
        <div className="stat-label">กำลังดำเนินการ</div>
      </div>
      <div className="stat-card overdue">
        <div className="stat-number">{stats.overdue || 0}</div>
        <div className="stat-label">เกินกำหนด</div>
      </div>
    </div>
  );

  const renderMonthView = () => {
    const days = getDaysInMonth(currentDate);
    const weekDays = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'];

    return (
      <div className="calendar-grid">
        <div className="weekdays">
          {weekDays.map(day => (
            <div key={day} className="weekday">{day}</div>
          ))}
        </div>
        
        <div className="days-grid">
          {days.map((day, index) => (
            <div
              key={index}
              className={`day-cell ${!day ? 'empty' : ''} ${
                day && day.toDateString() === selectedDate.toDateString() ? 'selected' : ''
              } ${
                day && day.toDateString() === new Date().toDateString() ? 'today' : ''
              }`}
              onClick={() => day && handleDateClick(day)}
            >
              {day && (
                <>
                  <div className="day-number">{day.getDate()}</div>
                  <div className="day-deadlines">
                    {getDeadlinesForDate(day).slice(0, 3).map(deadline => {
                      const color = deadline.category === 'PM' 
                        ? (statusColors[deadline.status] || '#607D8B')
                        : priorityColors[deadline.priority] || '#607D8B';
                      const baseDate = deadline.next_inspection_date || deadline.due_date;
                      const tip = deadline.category === 'PM'
                        ? `${deadline.title || ''}\nวันกำหนด: ${baseDate ? formatDate(new Date(baseDate)) : '-'}`
                        : (deadline.title || '');
                      const label = (deadline.title || '').length > 0 ? deadline.title.substring(0, 15) : 'รายการ';
                      return (
                        <div
                          key={deadline.id}
                          className="deadline-indicator"
                          style={{ backgroundColor: color }}
                          title={tip}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (deadline.category === 'PM') {
                              handleOpenPMDateEdit(deadline);
                            } else {
                              handleEditDeadline(deadline);
                            }
                          }}
                        >
                          {label}...
                        </div>
                      );
                    })}
                    {getDeadlinesForDate(day).length > 3 && (
                      <div className="more-deadlines">
                        +{getDeadlinesForDate(day).length - 3} เพิ่มเติม
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderListView = () => (
    <div className="list-view">
      {filteredDeadlines.length === 0 ? (
        <div className="no-deadlines">ไม่มีวันที่กำหนดในช่วงเวลานี้</div>
      ) : (
        filteredDeadlines.map(deadline => (
          <div key={deadline.id} className="deadline-item">
            <div className="deadline-header">
              <h3 className="deadline-title">{deadline.title}</h3>
              <div className="deadline-badges">
                <span 
                  className="priority-badge"
                  style={{ backgroundColor: priorityColors[deadline.priority] }}
                >
                  {deadline.priority === 'low' ? 'ต่ำ' :
                   deadline.priority === 'medium' ? 'ปานกลาง' :
                   deadline.priority === 'high' ? 'สูง' : 'เร่งด่วน'}
                </span>
                <span 
                  className="status-badge"
                  style={{ backgroundColor: 
                    (() => {
                      // Check if overdue
                      const dueDate = new Date(deadline.due_date || deadline.next_inspection_date);
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      dueDate.setHours(0, 0, 0, 0);
                      
                      if (!isNaN(dueDate.getTime()) && dueDate < today && deadline.status !== 'completed') {
                        return '#F44336'; // Red for overdue
                      }
                      return statusColors[deadline.status] || '#607D8B';
                    })()
                  }}
                >
                  {(() => {
                    // Check if overdue
                    const dueDate = new Date(deadline.due_date || deadline.next_inspection_date);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    dueDate.setHours(0, 0, 0, 0);
                    
                    if (!isNaN(dueDate.getTime()) && dueDate < today && deadline.status !== 'completed') {
                      return 'เกินกำหนด';
                    }
                    
                    return deadline.status === 'scheduled' || deadline.status === 'pending' ? 'กำหนดการ' :
                           deadline.status === 'in_progress' ? 'กำลังดำเนินการ' :
                           deadline.status === 'completed' ? 'เสร็จสิ้น' : 'ยกเลิก';
                  })()}
                </span>
              </div>
            </div>
            
            <div className="deadline-details">
              <div className="deadline-date">
                {(() => {
                  const base = deadline.due_date || deadline.next_inspection_date;
                  const d = base ? new Date(base) : null;
                  return (
                    <>
                      📅 {d && !isNaN(d) ? formatDate(d) : 'ไม่ระบุวันที่'} เวลา {d && !isNaN(d) ? formatTime(d) : 'ไม่ระบุเวลา'}
                    </>
                  );
                })()}
              </div>
              {deadline.category === 'PM' && (
                <div className="deadline-date">
                  🗓️ วันกำหนด (next_inspection_date): {formatDate(new Date(deadline.next_inspection_date || deadline.due_date))}
                </div>
              )}
              {deadline.category === 'PM' && deadline.customer_name && (
                <div className="deadline-category">
                  👤 ลูกค้า: {deadline.customer_name}
                </div>
              )}
              {deadline.category && (
                <div className="deadline-category">
                  🏷️ {deadline.category}
                </div>
              )}
              {deadline.description && (
                <div className="deadline-description">
                  {deadline.description}
                </div>
              )}
            </div>
            
            <div className="deadline-actions">
              <button 
                onClick={() => handleEditDeadline(deadline)}
                className="edit-btn"
              >
                แก้ไข
              </button>
              {deadline.category === 'PM' && (
                <>
                  <button 
                    onClick={() => handleOpenPMDateEdit(deadline)}
                    className="update-pm-btn"
                    title="แก้ไขวันที่กำหนด PM"
                  >
                    📅 แก้ไขวันที่ PM
                  </button>
                  <button 
                    onClick={() => handleDeletePMSchedule(deadline)}
                    className="delete-btn"
                    title="ลบ PM Schedule"
                  >
                    🗑️ ลบ
                  </button>
                </>
              )}
              {deadline.category !== 'PM' && (
                <button 
                  onClick={() => handleDeleteDeadline(deadline)}
                  className="delete-btn"
                  title="ลบ Deadline"
                >
                  🗑️ ลบ
                </button>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="calendar-page">
        <div className="loading">กำลังโหลด...</div>
      </div>
    );
  }

  return (
    <div className="calendar-page">
      <div className="page-header">
        <h1>📅 ปฏิทินวันที่กำหนด</h1>
        {renderStats()}
      </div>
      
      {renderCalendarHeader()}
      {renderFilters()}
      
      <div className="calendar-content">
        {viewMode === 'month' && renderMonthView()}
        {viewMode === 'list' && renderListView()}
        {filteredDeadlines.length === 0 && (
          <div className="no-deadlines">ไม่พบรายการในช่วงนี้ ลองเลือก "ทั้งหมด" ที่ตัวกรองสถานะ หรือเปลี่ยนช่วงเวลา</div>
        )}
      </div>
      
      {showAddModal && (
        <DeadlineModal
          deadline={selectedDeadline}
          onClose={() => setShowAddModal(false)}
          onSave={() => {
            setShowAddModal(false);
            fetchDeadlines();
            calculateStats();
          }}
        />
      )}

      {showPMDateEditModal && selectedPMData && (
        <PMDateEditModal
          isOpen={showPMDateEditModal}
          pmData={selectedPMData}
          onClose={() => {
            setShowPMDateEditModal(false);
            setSelectedPMData(null);
          }}
          onSave={handleSavePMDate}
        />
      )}
    </div>
  );
};

// Deadline Modal Component
const DeadlineModal = ({ deadline, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    due_date: '',
    priority: 'medium',
    category: '',
    status: 'pending',
    reminder_days: 7
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (deadline) {
      // แปลงวันที่จากปฏิทินไทยเป็นปฏิทินสากลสำหรับ input
      let formattedDate = '';
      if (deadline.due_date) {
        try {
          const date = new Date(deadline.due_date);
          // ตรวจสอบว่าเป็นปฏิทินไทยหรือไม่ (ปี > 2500)
          if (deadline.due_date.includes('/') && parseInt(deadline.due_date.split('/')[2]) > 2500) {
            // แปลงจากปฏิทินไทยเป็นปฏิทินสากล
            const [day, month, year] = deadline.due_date.split('/');
            const gregorianYear = parseInt(year) - 543;
            const gregorianDate = new Date(gregorianYear, parseInt(month) - 1, parseInt(day));
            formattedDate = gregorianDate.toISOString().slice(0, 16);
          } else {
            formattedDate = date.toISOString().slice(0, 16);
          }
        } catch (error) {
          console.error('Error parsing date:', error);
          formattedDate = '';
        }
      }
      
      setFormData({
        title: deadline.title || '',
        description: deadline.description || '',
        due_date: formattedDate,
        priority: deadline.priority || 'medium',
        category: deadline.category || '',
        status: deadline.status || 'pending',
        reminder_days: deadline.reminder_days || 7
      });
    }
  }, [deadline]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'กรุณากรอกชื่อเรื่อง';
    }
    
    if (!formData.due_date) {
      newErrors.due_date = 'กรุณาเลือกวันที่กำหนด';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    setErrors({});
    
    try {
      const url = deadline ? `/api/deadlines/${deadline.id}` : '/api/deadlines';
      const method = deadline ? 'PUT' : 'POST';
      
      // แปลงวันที่จากปฏิทินสากลเป็นปฏิทินไทยก่อนส่ง API
      let formattedDueDate = formData.due_date;
      if (formData.due_date) {
        try {
          const date = new Date(formData.due_date);
          const thaiYear = date.getFullYear() + 543;
          const day = String(date.getDate()).padStart(2, '0');
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          formattedDueDate = `${day}/${month}/${thaiYear}, ${hours}:${minutes}`;
        } catch (error) {
          console.error('Error formatting date:', error);
        }
      }
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          due_date: formattedDueDate,
          created_by: 1 // TODO: Get from user context
        }),
      });

      if (response.ok) {
        onSave();
      } else {
        const errorData = await response.json();
        setErrors({ submit: errorData.message || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล' });
      }
    } catch (error) {
      console.error('Error saving deadline:', error);
      setErrors({ submit: 'เกิดข้อผิดพลาดในการเชื่อมต่อกับเซิร์ฟเวอร์' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>{deadline ? 'แก้ไขวันที่กำหนด' : 'เพิ่มวันที่กำหนดใหม่'}</h2>
          <button onClick={onClose} className="close-btn">×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="deadline-form">
          <div className="form-group">
            <label>ชื่อเรื่อง *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className={errors.title ? 'error' : ''}
              disabled={isLoading}
              required
            />
            {errors.title && <span className="error-message">{errors.title}</span>}
          </div>
          
          <div className="form-group">
            <label>รายละเอียด</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows="3"
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>วันที่และเวลากำหนด *</label>
              <input
                type="datetime-local"
                value={formData.due_date}
                onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                className={errors.due_date ? 'error' : ''}
                disabled={isLoading}
                required
              />
              {errors.due_date && <span className="error-message">{errors.due_date}</span>}
              <small className="form-hint">รูปแบบ: วัน/เดือน/ปี ค.ศ. เวลา (เช่น 04/01/2026, 17:00)</small>
            </div>
            
            <div className="form-group">
              <label>ระดับความสำคัญ</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({...formData, priority: e.target.value})}
              >
                <option value="low">ต่ำ</option>
                <option value="medium">ปานกลาง</option>
                <option value="high">สูง</option>
                <option value="urgent">เร่งด่วน</option>
              </select>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>หมวดหมู่</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
              >
                <option value="">เลือกหมวดหมู่</option>
                <option value="maintenance">การบำรุงรักษา</option>
                <option value="installation">การติดตั้ง</option>
                <option value="report">รายงาน</option>
                <option value="training">การอบรม</option>
                <option value="inventory">สต็อกสินค้า</option>
              </select>
            </div>
            
            <div className="form-group">
              <label>สถานะ</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
              >
                <option value="pending">กำหนดการ</option>
                <option value="in_progress">กำลังดำเนินการ</option>
                <option value="completed">เสร็จสิ้น</option>
                <option value="cancelled">ยกเลิก</option>
              </select>
            </div>
          </div>
          
          <div className="form-group">
            <label>แจ้งเตือนล่วงหน้า (วัน)</label>
            <input
              type="number"
              value={formData.reminder_days}
              onChange={(e) => setFormData({...formData, reminder_days: parseInt(e.target.value)})}
              min="0"
              max="365"
            />
          </div>
          
          {errors.submit && (
            <div className="error-message submit-error">{errors.submit}</div>
          )}

          <div className="form-actions">
            <button type="button" onClick={onClose} className="cancel-btn" disabled={isLoading}>
              ยกเลิก
            </button>
            <button type="submit" className="save-btn" disabled={isLoading}>
              {isLoading ? 'กำลังบันทึก...' : (deadline ? 'บันทึกการแก้ไข' : 'เพิ่มวันที่กำหนด')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CalendarPage;